import type { ComponentType, ReactNode } from 'react';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// FR-8: the ONE shared multi-color ("rainbow") palette for chart SERIES only. Everything else in the
// UI stays monochrome (ink/paper/line/muted) — do not use these for non-chart elements.
export const CHART_PALETTE = [
  '#2563eb', // blue
  '#059669', // green
  '#d97706', // amber
  '#dc2626', // red
  '#7c3aed', // violet
  '#0891b2', // cyan
  '#db2777', // pink
] as const;

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-5">
      <div>
        <h1 className="text-[28px] text-ink">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[15px] text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('card', className)}>{children}</div>;
}

// Monochrome chips — emphasis via fill (ink) vs hairline (outline), not colour (theme brief).
type Tone = 'outline' | 'solid' | 'muted';

export function Badge({ children, tone = 'outline' }: { children: ReactNode; tone?: Tone }) {
  const cls =
    tone === 'solid'
      ? 'bg-ink text-paper border-ink'
      : tone === 'muted'
        ? 'text-muted border-line'
        : 'text-ink border-line';
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-pill border px-2.5 py-0.5 text-xs font-medium',
        cls,
      )}
    >
      {children}
    </span>
  );
}

// "Caught"/terminal events get the filled ink chip to draw the eye; everything else is a hairline.
export function statusTone(s: string): Tone {
  if (['clicked', 'submitted_data', 'submitted', 'failed', 'bounced'].includes(s)) return 'solid';
  return 'outline';
}

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
}: {
  label: string;
  value: string;
  icon: ComponentType<{ className?: string }>;
  hint?: string;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <Icon className="h-4 w-4 text-muted" />
      </div>
      <div className="mt-3 font-display text-4xl tabular-nums text-ink">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </Card>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  hint,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <Icon className="h-6 w-6 text-muted" />
      <div className="text-[15px] font-medium text-ink">{title}</div>
      {hint && <div className="max-w-sm text-sm text-muted">{hint}</div>}
    </div>
  );
}

export function Spinner() {
  return (
    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-line border-t-ink" />
  );
}
